import React from 'react';
import {
  View,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  Animated,
  StyleProp,
  ViewStyle,
} from 'react-native';
import Text from '@/components/basic/text';
import theme from '@/style';
import {LotteryMode} from '../mix-lottery-service';

export interface BetTabProps {
  tabList?: {label?: string; key: `${LotteryMode}`}[];
  selectedKey?: string;
  onChange?: (key: `${LotteryMode}`) => void;
  style?: StyleProp<ViewStyle>;
}

const BetTab = ({tabList = [], selectedKey, onChange, style}: BetTabProps) => {
  const isWeb = typeof document !== 'undefined';
  const [measure, setMeasure] = React.useState<number[]>([]);
  const transAnim = React.useRef(new Animated.Value(0)).current;
  const scrollRef = React.useRef<ScrollView>(null);
  const onMeasure = (i: number, value: number) => {
    measure[i] = value;
    setMeasure([...measure]);
  };

  React.useEffect(() => {
    if (measure.length > 0 && tabList.length === measure.length) {
      const index = tabList.findIndex(item => item.key === selectedKey);
      onTrans(measure[index]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measure, tabList, selectedKey]);

  const onTrans = (v: number) => {
    Animated.timing(transAnim, {
      toValue: v,
      duration: 200,
      useNativeDriver: true,
    }).start();
  };

  if (isWeb) {
    return (
      <div style={{...stylesWeb.container, ...(style as React.CSSProperties)}}>
        <div style={stylesWeb.scrollContent}>
          {tabList.map((item, _i) => {
            const isSelected = item.key === selectedKey;
            return (
              <button
                key={_i}
                onClick={() => onChange?.(item.key)}
                style={stylesWeb.tabButton}
                type="button">
                <span
                  style={{
                    ...stylesWeb.tabText,
                    ...(isSelected
                      ? stylesWeb.tabTextActive
                      : stylesWeb.tabTextInactive),
                  }}>
                  {item.label}
                </span>
                {isSelected ? <div style={stylesWeb.cursor} /> : null}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <View style={[styles.container, style]}>
      <ScrollView
        showsHorizontalScrollIndicator={false}
        horizontal
        ref={scrollRef}
        contentContainerStyle={styles.scrollContent}>
        {tabList.map((item, _i) => {
          const isSelected = item.key === selectedKey;
          return (
            <TouchableOpacity
              onPress={() => {
                onChange?.(item.key);
              }}
              key={_i}
              activeOpacity={0.8}
              style={styles.tabButton}
              onLayout={e => {
                const {width, x} = e.nativeEvent.layout;
                const defaultW = 20;
                const target = (width - defaultW) / 2 + x;
                onMeasure(_i, target);
              }}>
              <Text
                color={
                  isSelected ? theme.basicColor.dark : theme.fontColor.second
                }
                size="medium"
                fontWeight={isSelected ? '700' : '500'}>
                {item.label}
              </Text>
            </TouchableOpacity>
          );
        })}
        <Animated.View
          style={[
            styles.square,
            {
              transform: [{translateX: transAnim}],
            },
          ]}
        />
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#fff',
    minHeight: 48,
    paddingHorizontal: 12,
    paddingBottom: 8,
    borderBottomColor: '#D8E0EA',
    borderBottomWidth: 1,
    justifyContent: 'flex-end',
  },
  scrollContent: {
    gap: 32,
    paddingBottom: 0,
    alignItems: 'flex-end',
  },
  tabButton: {
    height: 28,
    justifyContent: 'center',
  },
  square: {
    width: 20,
    height: 4,
    position: 'absolute',
    left: 0,
    bottom: 0,
    backgroundColor: theme.basicColor.primary,
  },
});

const stylesWeb: Record<string, React.CSSProperties> = {
  container: {
    backgroundColor: '#fff',
    minHeight: '3rem',
    padding: '0.75rem 0 0.5rem',
    borderBottom: '1px solid #D8E0EA',
    display: 'flex',
    alignItems: 'flex-end',
    overflowX: 'auto',
    overflowY: 'hidden',
    boxSizing: 'border-box',
  },
  scrollContent: {
    display: 'inline-flex',
    gap: '2rem',
    alignItems: 'flex-end',
    minWidth: '100%',
    boxSizing: 'border-box',
    padding: '0 0.75rem',
  },
  tabButton: {
    position: 'relative',
    height: '1.75rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    padding: 0,
    whiteSpace: 'nowrap',
  },
  tabText: {
    fontSize: '0.875rem',
    lineHeight: '1.25rem',
  },
  tabTextActive: {
    color: theme.basicColor.dark,
    fontWeight: 700,
  },
  tabTextInactive: {
    color: theme.fontColor.second,
    fontWeight: 500,
  },
  cursor: {
    width: '1.25rem',
    height: '0.25rem',
    position: 'absolute',
    left: '50%',
    bottom: '-0.5rem',
    transform: 'translateX(-50%)',
    backgroundColor: theme.basicColor.primary,
  },
};

export default BetTab;
